import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { useCurrentIdentity } from '@/lib/identity-context';

interface DeleteSiteButtonProps {
  siteId: string;
  siteName: string;
  size?: 'sm' | 'default';
}

export function DeleteSiteButton({ siteId, siteName, size = 'sm' }: DeleteSiteButtonProps) {
  const { hasPermission } = useCurrentIdentity();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.DELETE('/sites/{siteId}', {
        params: { path: { siteId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      // Remove per-site caches first so the list invalidation below
      // doesn't trigger refetches for the deleted site (those would
      // 404 and spam the console). Then invalidate only the list.
      queryClient.removeQueries({ queryKey: queryKeys.sites.detail(siteId) });
      queryClient.removeQueries({ queryKey: queryKeys.sites.drift(siteId) });
      queryClient.removeQueries({ queryKey: queryKeys.sites.baseline(siteId) });
      queryClient.removeQueries({ queryKey: queryKeys.sites.crawls(siteId) });
      queryClient.removeQueries({ queryKey: queryKeys.sites.scanUrls(siteId) });
      queryClient.invalidateQueries({ queryKey: ['sites', 'list'] });
    },
  });

  if (!hasPermission('sites:write')) {
    return null;
  }

  const onClick = () => {
    if (mutation.isPending) return;
    const ok = window.confirm(
      `Delete site "${siteName}"?\n\nThis removes the site from monitoring. Scan history and findings are preserved on the backend but will no longer be reachable through the dashboard.`,
    );
    if (ok) mutation.mutate();
  };

  return (
    <Button
      size={size}
      variant="outline"
      onClick={onClick}
      disabled={mutation.isPending}
      data-testid={`delete-site-${siteId}`}
      title={mutation.isError ? 'Delete failed — see console' : 'Delete site'}
    >
      <Trash2 className="h-3.5 w-3.5" />
      {mutation.isPending ? 'Deleting…' : 'Delete'}
    </Button>
  );
}
